import Ascending from '@assets/images/sorting/Ascending.svg';
import Descending from '@assets/images/sorting/Descending.svg';
import Unsorted from '@assets/images/sorting/Unsorted.svg';

import {
  Table as MuiTable,
  TableBody as MuiTableBody,
  TableCell as MuiTableCell,
  type TableCellProps as MuiTableCellProps,
  TableHead as MuiTableHead,
  type TableHeadProps as MuiTableHeadProps,
  TableRow as MuiTableRow,
  type TableRowProps as MuiTableRowProps,
  type SortDirection,
  type SxProps,
  type TableBodyProps,
  type TableProps,
} from '@mui/material';
import { Link } from '@ovComponents/2-component/orgLink/orgLink';
import {
  Box,
  IconButton,
  Typography,
  type TypographyProps,
  type TypographyVariant,
  useTheme,
} from 'hephaestus/web';
import {
  HorizontalRule as HorizontalRuleIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  UnfoldLess as UnfoldLessIcon,
  UnfoldMore as UnfoldMoreIcon,
} from 'hephaestus/webIcons';
import { omit } from 'lodash-es';
import {
  type ComponentProps,
  createContext,
  type FC,
  forwardRef,
  type MouseEvent,
  type ReactNode,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTableTokens } from './table.tokens';

export type TableCellProps = MuiTableCellProps & {
  dense?: boolean;
  number?: boolean;
  date?: boolean;
  right?: boolean;
  bold?: boolean;
  maxWidth?: string;
  leadingComponent?: ReactNode;
  isSortable?: boolean;
  isFirst?: boolean;
  isGrouped?: boolean;
  'data-testid'?: string;
  center?: boolean;
  backgroundColor?: string;
  textColor?: string;
  /** Table head only: overrides inner label typography (default labelSmall / bold). */
  headLabelVariant?: TypographyVariant;
  headLabelWeight?: 'regular' | 'bold';
  headLabelSx?: SxProps;
  /** Table head only: render children without inner Typography (e.g. checkbox column). */
  headerBare?: boolean;
};

type TableRowProps = MuiTableRowProps & {
  pointer?: boolean;
  backgroundColor?: string;
  showHoverElements?: boolean;
  /** When set, middle-click and Ctrl/Cmd+click open this URL in a new tab. */
  href?: string;
};

const extraTableCellProps = [
  'dense',
  'number',
  'date',
  'right',
  'bold',
  'maxWidth',
  'leadingComponent',
  'headLabelVariant',
  'headLabelWeight',
  'headLabelSx',
  'headerBare',
  'textColor',
];

export type ExpandableRowControl = {
  instanceId: string;
  parentInstanceId: string | null;
  opened: boolean;
  open: () => void;
  close: () => void;
};

const collectDescendantInstanceIds = (
  rowsById: ReadonlyMap<string, ExpandableRowControl>,
  rootInstanceId: string,
): string[] => {
  const childrenByParent = new Map<string, string[]>();
  for (const [id, row] of rowsById) {
    const p = row.parentInstanceId;
    if (p == null) {
      continue;
    }
    if (!childrenByParent.has(p)) {
      childrenByParent.set(p, []);
    }
    childrenByParent.get(p)!.push(id);
  }
  const out: string[] = [];
  const stack = [...(childrenByParent.get(rootInstanceId) ?? [])];
  while (stack.length > 0) {
    const id = stack.pop();
    if (id === undefined) {
      break;
    }
    out.push(id);
    const nested = childrenByParent.get(id);
    if (nested) {
      stack.push(...nested);
    }
  }
  return out;
};

type ExpandableTableDispatchValue = {
  syncExpandableRow: (rowId: string, control: ExpandableRowControl) => void;
  unregisterExpandableRow: (rowId: string) => void;
  /** Latest row registry without subscribing (for subtree expand). */
  getExpandableRowsSnapshot: () => ReadonlyMap<string, ExpandableRowControl>;
  /** Expand or collapse this row and all nested branch rows (registry parent links). */
  toggleExpandableSubtree: (rootInstanceId: string) => void;
};

/** Stable across map updates — used by {@link ExpandableTableRow} so effects do not churn. */
const ExpandableTableDispatchContext =
  createContext<ExpandableTableDispatchValue | null>(null);

const ExpandableTableRowsStateContext = createContext<ReadonlyMap<
  string,
  ExpandableRowControl
> | null>(null);

const ExpandableTableProvider = ({ children }: { children: ReactNode }) => {
  const [expandableRowsById, setExpandableRowsById] = useState<
    Map<string, ExpandableRowControl>
  >(() => new Map());

  const expandableRowsSnapshotRef = useRef(expandableRowsById);
  expandableRowsSnapshotRef.current = expandableRowsById;

  const getExpandableRowsSnapshot = useCallback(
    () => expandableRowsSnapshotRef.current,
    [],
  );

  const syncExpandableRow = useCallback(
    (rowId: string, control: ExpandableRowControl) => {
      setExpandableRowsById((prev) => {
        const existing = prev.get(rowId);
        if (
          existing &&
          existing.instanceId === control.instanceId &&
          existing.parentInstanceId === control.parentInstanceId &&
          existing.opened === control.opened &&
          existing.open === control.open &&
          existing.close === control.close
        ) {
          return prev;
        }
        const next = new Map(prev);
        next.set(rowId, control);
        return next;
      });
    },
    [],
  );

  const unregisterExpandableRow = useCallback((rowId: string) => {
    setExpandableRowsById((prev) => {
      if (!prev.has(rowId)) {
        return prev;
      }
      const next = new Map(prev);
      next.delete(rowId);
      return next;
    });
  }, []);

  const toggleExpandableSubtree = useCallback((rootInstanceId: string) => {
    const map = expandableRowsSnapshotRef.current;
    const descendantIds = collectDescendantInstanceIds(map, rootInstanceId);
    const subtreeBranchIds = [rootInstanceId, ...descendantIds].filter((id) =>
      map.has(id),
    );
    if (subtreeBranchIds.length === 0) {
      return;
    }
    const allOpen = subtreeBranchIds.every((id) => map.get(id)?.opened);
    if (allOpen) {
      subtreeBranchIds.forEach((id) => map.get(id)?.close());
    } else {
      map.get(rootInstanceId)?.open();
      descendantIds.forEach((id) => map.get(id)?.open());
    }
  }, []);

  const dispatchValue = useMemo(
    (): ExpandableTableDispatchValue => ({
      syncExpandableRow,
      unregisterExpandableRow,
      getExpandableRowsSnapshot,
      toggleExpandableSubtree,
    }),
    [
      syncExpandableRow,
      unregisterExpandableRow,
      getExpandableRowsSnapshot,
      toggleExpandableSubtree,
    ],
  );

  return (
    <ExpandableTableDispatchContext.Provider value={dispatchValue}>
      <ExpandableTableRowsStateContext.Provider value={expandableRowsById}>
        {children}
      </ExpandableTableRowsStateContext.Provider>
    </ExpandableTableDispatchContext.Provider>
  );
};

/**
 * Expandable rows registered by {@link ExpandableTableRow} when nested under
 * {@link Table}. Keys are each row instance id (see `useRef` in expandable row).
 */
export const useExpandableTableRowControls = (): ReadonlyMap<
  string,
  ExpandableRowControl
> => {
  const rows = useContext(ExpandableTableRowsStateContext);
  return rows ?? new Map();
};

type ExpandableSubtreeToggleProps = {
  rootInstanceId: string;
};

/**
 * Branch-only control: same expand/collapse-all semantics as {@link ExpandableTableRowToggle},
 * scoped to one subtree via {@link ExpandableTableDispatchContext.toggleExpandableSubtree}.
 */
const ExpandableSubtreeToggle = ({
  rootInstanceId,
}: ExpandableSubtreeToggleProps) => {
  const dispatch = useContext(ExpandableTableDispatchContext);
  const rowsById = useExpandableTableRowControls();

  const allSubtreeOpen = useMemo(() => {
    const descendantIds = collectDescendantInstanceIds(
      rowsById,
      rootInstanceId,
    );
    const ids = [rootInstanceId, ...descendantIds].filter((id) =>
      rowsById.has(id),
    );
    return ids.length > 0 && ids.every((id) => rowsById.get(id)?.opened);
  }, [rowsById, rootInstanceId]);

  const handleClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      dispatch?.toggleExpandableSubtree(rootInstanceId);
    },
    [dispatch, rootInstanceId],
  );

  return (
    <IconButton
      aria-label='collapse'
      data-testid='expand-subtree-icon'
      onClick={handleClick}
      size='xsmall'
    >
      {allSubtreeOpen ? (
        <UnfoldLessIcon fontSize='inherit' />
      ) : (
        <UnfoldMoreIcon fontSize='inherit' />
      )}
    </IconButton>
  );
};

export type ExpandableTableRowToggleProps = Omit<
  ComponentProps<typeof IconButton>,
  'children'
> & {
  'data-testid'?: string;
};

/**
 * IconButton that expands or collapses all expandable rows in the table.
 * Requires nested rows to stay mounted (see {@link ExpandableTableRow} `ancestorsOpen`).
 * Must render under {@link Table}.
 */
export const ExpandableTableRowToggle = ({
  disabled,
  onClick,
  'data-testid': dataTestId = 'expandable-table-row-toggle',
  ...rest
}: ExpandableTableRowToggleProps) => {
  const expandableRowsById = useExpandableTableRowControls();

  const controls = useMemo(
    () => [...expandableRowsById.values()],
    [expandableRowsById],
  );

  const hasRows = controls.length > 0;
  const allOpened = hasRows && controls.every((control) => control.opened);

  const handleClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      if (!hasRows) {
        return;
      }
      if (allOpened) {
        controls.forEach((control) => control.close());
      } else {
        controls.forEach((control) => control.open());
      }
      onClick?.(event);
    },
    [allOpened, controls, hasRows, onClick],
  );

  return (
    <IconButton
      aria-label='collapse'
      data-testid={dataTestId}
      disabled={Boolean(disabled) || !hasRows}
      onClick={handleClick}
      {...rest}
    >
      {allOpened ? (
        <UnfoldLessIcon fontSize='inherit' />
      ) : (
        <UnfoldMoreIcon fontSize='inherit' />
      )}
    </IconButton>
  );
};

export const Table = (props: TableProps) => (
  <ExpandableTableProvider>
    <MuiTable
      {...props}
      sx={[
        { overflow: 'auto' },
        ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
      ]}
    />
  </ExpandableTableProvider>
);

export const TableBody = forwardRef<HTMLTableSectionElement, TableBodyProps>(
  (props, ref) => (
    <MuiTableBody
      {...props}
      ref={ref}
      sx={[
        { display: 'table-row-group', width: '100%' },
        ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
      ]}
    />
  ),
);

TableBody.displayName = 'TableBody';

export const TableHead = (props: MuiTableHeadProps) => (
  <MuiTableHead
    {...props}
    sx={[
      { display: 'table-header-group', width: '100%' },
      ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
    ]}
  />
);

export const TableRow = forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ showHoverElements, href, ...restProps }, ref) => {
    const theme = useTheme();

    const sx = {
      backgroundColor:
        restProps.backgroundColor || `${theme.color.surface} !important`,
      '&:hover': {
        backgroundColor:
          restProps.hover || href
            ? `${theme.color.surfaceContainerVariant} !important`
            : undefined,
        cursor: restProps.pointer || href ? 'pointer' : undefined,
      },
      ...(showHoverElements && {
        '& .hover-element': {
          opacity: 0,
          transition: 'opacity 0.2s ease-in-out',
        },
        '&:hover .hover-element': {
          opacity: 1,
        },
      }),
    };

    if (href) {
      return (
        <MuiTableRow
          {...restProps}
          component={Link as any}
          to={href}
          ref={ref}
          sx={{
            ...sx,
            display: 'table-row',
            textDecoration: 'none',
            color: 'inherit',
          }}
        />
      );
    }

    return <MuiTableRow {...restProps} ref={ref} sx={sx} />;
  },
);

TableRow.displayName = 'TableRow';

export const TableCell = (props: TableCellProps) => {
  const theme = useTheme();
  const tokens = useTableTokens(theme);
  const { sx, isFirst, isGrouped, ...restProps } = props;

  const baseSx: SxProps = {
    borderBottom: `1px solid ${theme.color.outlineVariant}`,
    height: !props.dense ? '32px' : 'auto',
    textAlign:
      props.right || props.number ? 'right' : props.center ? 'center' : 'left',
    color: tokens.cellTextColor,
  };

  const composedSx = [
    baseSx,
    ...(isFirst
      ? [
          {
            left: 0,
            position: 'sticky',
            backgroundColor: 'inherit',
            ...(isGrouped ? { paddingLeft: '44px' } : {}),
          },
        ]
      : []),
    ...(Array.isArray(sx) ? sx : [sx]),
  ];

  return (
    <MuiTableCell
      sx={composedSx}
      {...omit(restProps, extraTableCellProps)}
      data-testid={props['data-testid']}
    >
      <Typography
        variant='bodySmall'
        sx={{
          fontWeight: props.bold ? 600 : undefined,
          fontVariant: props.number ? 'tabular-nums' : 'normal',
          maxWidth: props.maxWidth,
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          color: 'inherit',
          whiteSpace: props.number || props.date ? 'nowrap' : undefined,
          ...(props.leadingComponent
            ? {
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                justifyContent: props.right ? 'end' : 'start',
              }
            : {}),
        }}
        data-testid={`${props['data-testid']}-content`}
      >
        {props.leadingComponent ? (
          <>
            <Box>{props.leadingComponent}</Box>
            <Box>{props.children}</Box>
          </>
        ) : (
          props.children
        )}
      </Typography>
    </MuiTableCell>
  );
};

const SortIcon = ({ direction }: { direction: SortDirection | undefined }) => {
  const icon =
    direction === 'asc'
      ? Ascending
      : direction === 'desc'
        ? Descending
        : Unsorted;

  return (
    <img
      src={icon}
      alt={direction || 'unsorted'}
      style={{ marginLeft: '8px' }}
    />
  );
};

export const TableHeadCell = (props: TableCellProps) => {
  const theme = useTheme();
  const tokens = useTableTokens(theme);
  const {
    sx,
    isFirst,
    center,
    backgroundColor,
    textColor,
    headLabelVariant = 'labelSmall',
    headLabelWeight = 'bold',
    headLabelSx,
    headerBare,
    ...restProps
  } = props;

  const baseSx: SxProps = {
    outline: `1px solid ${theme.color.surface}`,
    outlineOffset: '-1px',
    borderBottom: 'none',
    textAlign:
      props.right || props.number ? 'right' : props.center ? 'center' : 'left',
    verticalAlign: 'bottom',
    background: backgroundColor || tokens.headBackgroundColor,
    padding: '16px 16px',
    cursor: props.isSortable ? 'pointer' : 'default',
    '&:hover': {
      background: props.isSortable
        ? tokens.headHoverBackgroundColor
        : tokens.headBackgroundColor,
    },
  };

  const composedSx = [
    baseSx,
    ...(isFirst ? [{ position: 'sticky', left: 0 }] : []),
    ...(Array.isArray(sx) ? sx : [sx]),
  ];

  return (
    <MuiTableCell
      sx={composedSx}
      {...omit(restProps, extraTableCellProps)}
      data-testid={props['data-testid']}
    >
      <Box
        onClick={(e: any) => {
          props.isSortable && props.onClick && props.onClick(e);
        }}
        data-testid={props.isSortable ? `${props['data-testid']}-sortable` : ''}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: props.right
            ? 'end'
            : props.center
              ? 'center'
              : 'start',
        }}
      >
        {headerBare ? (
          props.children
        ) : (
          <Typography
            variant={headLabelVariant}
            sx={[
              {
                fontWeight: headLabelWeight === 'bold' ? 600 : undefined,
                color: textColor || tokens.headTextColor,
              },
              ...(headLabelSx != null
                ? Array.isArray(headLabelSx)
                  ? headLabelSx
                  : [headLabelSx]
                : []),
            ]}
            data-testid={`${props['data-testid']}-label`}
          >
            {props.children}
          </Typography>
        )}
        {props.isSortable && <SortIcon direction={props.sortDirection} />}
      </Box>
    </MuiTableCell>
  );
};

export const TableTotalCell = (props: TableCellProps) => {
  const theme = useTheme();
  const { sx, isFirst, isGrouped, ...restProps } = props;

  const baseSx: any = {
    borderTop: `1px solid ${theme.color.outline}`,
    borderBottom: isGrouped
      ? `1px solid ${theme.color.outlineVariant}`
      : 'none',
    height: !props.dense ? '32px' : 'auto',
    textAlign: props.right || props.number ? 'right' : 'left',
    backgroundColor: isGrouped ? 'inherit' : theme.color.background,
    ...(!isGrouped && {
      '&:last-of-type': {
        borderRadius: `0 0 ${theme.borderRadius.xl} 0`,
      },
      '&:first-of-type': {
        borderRadius: `0 0 0 ${theme.borderRadius.xl}`,
      },
    }),
    ...(isFirst ? { position: 'sticky', left: 0 } : {}),
    ...props.sx,
  };

  return (
    <MuiTableCell
      sx={baseSx}
      {...omit(restProps, extraTableCellProps)}
      data-testid={props['data-testid']}
    >
      <Typography
        data-testid={`${props['data-testid']}-content`}
        variant='bodySmall'
        sx={{
          fontWeight: props.bold || isGrouped ? 600 : undefined,
          fontVariant: props.number ? 'tabular-nums' : 'normal',
          maxWidth: props.maxWidth,
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          color: 'inherit',
          ...(props.leadingComponent
            ? {
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                justifyContent: props.right ? 'end' : 'start',
              }
            : {}),
        }}
      >
        {props.leadingComponent ? (
          <>
            <Box>{props.leadingComponent}</Box>
            <Box>{props.children}</Box>
          </>
        ) : (
          props.children
        )}
      </Typography>
    </MuiTableCell>
  );
};

export type ExpandableRow = {
  id: string;
  name: string;
  level: number;
  href?: string;
  nameHref?: string;
  expandedRows?: ExpandableRow[];
};

/** True if `row` has at least one descendant that itself has children (nested levels). */
const expandableRowHasNestedExpandableDescendant = (
  row: ExpandableRow,
): boolean => {
  if (!row.expandedRows?.length) {
    return false;
  }
  for (const child of row.expandedRows) {
    if (child.expandedRows && child.expandedRows.length > 0) {
      return true;
    }
    if (expandableRowHasNestedExpandableDescendant(child)) {
      return true;
    }
  }
  return false;
};

type Props<T extends ExpandableRow> = {
  row: T;
  DataComponent: FC<{ row: T }>;
  textColumnStyles?: TypographyProps['sx'];
  tableCellStyles?: SxProps;
  onRowClick?: (row: T) => void;
  /**
   * Instance id of the parent {@link ExpandableTableRow} (omit on root rows).
   */
  parentExpandableRowInstanceId?: string | null;
  /**
   * When false, every ancestor with children is collapsed so this row should not
   * render its {@link TableRow}. Descendants stay mounted for expand-all/collapse-all.
   */
  ancestorsOpen?: boolean;
};

export const ExpandableTableRow = <T extends ExpandableRow>({
  row,
  DataComponent,
  textColumnStyles,
  tableCellStyles = {},
  onRowClick,
  parentExpandableRowInstanceId = null,
  ancestorsOpen = true,
  ...tableRowProps
}: Props<T> & TableRowProps) => {
  const expandableRowDispatch = useContext(ExpandableTableDispatchContext);
  const theme = useTheme();

  const expandableRowRef = useRef<string>(crypto.randomUUID());
  const [isExpanded, setIsExpanded] = useState(false);
  const openRow = useCallback(() => setIsExpanded(true), []);
  const closeRow = useCallback(() => setIsExpanded(false), []);

  useLayoutEffect(() => {
    if (!expandableRowDispatch) {
      return;
    }
    const rowId = expandableRowRef.current;
    if (!row.expandedRows?.length) {
      expandableRowDispatch.unregisterExpandableRow(rowId);
      return;
    }
    expandableRowDispatch.syncExpandableRow(rowId, {
      instanceId: rowId,
      parentInstanceId: parentExpandableRowInstanceId,
      opened: isExpanded,
      open: openRow,
      close: closeRow,
    });
  }, [
    expandableRowDispatch,
    isExpanded,
    openRow,
    closeRow,
    parentExpandableRowInstanceId,
    row.expandedRows?.length,
  ]);

  useLayoutEffect(() => {
    if (!expandableRowDispatch) {
      return;
    }
    const rowId = expandableRowRef.current;
    return () => {
      expandableRowDispatch.unregisterExpandableRow(rowId);
    };
  }, [expandableRowDispatch]);

  const onExpandClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  const childAncestorsOpen = ancestorsOpen && isExpanded;

  const showSubtreeMaster = expandableRowHasNestedExpandableDescendant(row);

  return (
    <>
      {ancestorsOpen ? (
        <TableRow
          onClick={() => {
            if (onRowClick) onRowClick(row);
          }}
          href={row.href}
          {...tableRowProps}
        >
          <TableCell
            sx={{
              ...tableCellStyles,
              display: 'flex',
              alignItems: 'center',
              pl: `${row.level * 20}px`,
              whiteSpace: 'nowrap',
              ...(showSubtreeMaster ? { overflow: 'visible' } : {}),
            }}
          >
            <Box sx={{ display: 'inline', ml: 1, mr: 1 }}>
              {row.expandedRows && row.expandedRows.length > 0 ? (
                <Box
                  sx={{
                    position: 'relative',
                    display: 'inline-block',
                    alignItems: 'center',
                    ...(showSubtreeMaster
                      ? {
                          '&:hover .expandable-row-subtree-btn': {
                            opacity: 1,
                            pointerEvents: 'auto',
                          },
                        }
                      : {}),
                  }}
                >
                  {showSubtreeMaster ? (
                    <Box
                      className='expandable-row-subtree-btn'
                      sx={{
                        left: '100%',
                        p: '8px',
                        pl: '4px',
                        position: 'absolute',
                        opacity: 0,
                        pointerEvents: 'none',
                        transition: 'opacity 120ms ease',
                        zIndex: 2,
                      }}
                    >
                      <Box
                        sx={{
                          position: 'relative',
                          backgroundColor: theme.color.background,
                          boxShadow: '0 0 8px rgba(0,0,0,0.1)',
                          borderRadius: '100%',
                        }}
                      >
                        <ExpandableSubtreeToggle
                          rootInstanceId={expandableRowRef.current}
                        />
                      </Box>
                    </Box>
                  ) : null}
                  <IconButton
                    aria-label='collapse'
                    onClick={onExpandClick}
                    data-testid='expand-icon'
                  >
                    {isExpanded ? (
                      <KeyboardArrowUpIcon fontSize='inherit' />
                    ) : (
                      <KeyboardArrowDownIcon fontSize='inherit' />
                    )}
                  </IconButton>
                </Box>
              ) : (
                <IconButton aria-hidden disabled>
                  <HorizontalRuleIcon fontSize='inherit' />
                </IconButton>
              )}
            </Box>
            {row.nameHref ? (
              <Link
                to={row.nameHref}
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                style={{ textDecoration: 'none' }}
              >
                <Typography
                  variant='bodySmall'
                  component='p'
                  sx={[
                    textColumnStyles as any,
                    {
                      display: 'inline',
                      color: theme.color.primary,
                      '&:hover': { textDecoration: 'underline' },
                    },
                  ]}
                >
                  {row.name}
                </Typography>
              </Link>
            ) : (
              <Typography
                variant='bodySmall'
                component='p'
                sx={[textColumnStyles as any, { display: 'inline' }]}
              >
                {row.name}
              </Typography>
            )}
          </TableCell>
          <DataComponent row={row} />
        </TableRow>
      ) : null}
      {row.expandedRows?.map((child) => (
        <ExpandableTableRow
          key={child.id}
          row={child as T}
          ancestorsOpen={childAncestorsOpen}
          parentExpandableRowInstanceId={expandableRowRef.current}
          DataComponent={DataComponent}
          textColumnStyles={textColumnStyles}
          tableCellStyles={tableCellStyles}
          onRowClick={onRowClick}
          {...tableRowProps}
        />
      ))}
    </>
  );
};
